/**
 * Pure scroll-target math for KeyboardAwareScrollView.
 *
 * Extracted from the component so it can be unit-tested without RN /
 * measureInWindow. Given the measured geometry of a focused input, the scroll
 * view, the keyboard, and the content, it returns the scroll offset that puts
 * the input's centre at the vertical midpoint of the visible area above the
 * keyboard — clamped to the valid scroll range so we never over-scroll.
 */

export interface KeyboardScrollInput {
  /** Input's screen-space Y (from measureInWindow). */
  inputScreenY: number
  /** Input's measured height. */
  inputHeight: number
  /** Scroll view's screen-space top Y. */
  scrollViewScreenY: number
  /** Scroll view's measured height. */
  scrollViewScreenHeight: number
  /** Current keyboard height (0 when hidden / on desktop web). */
  keyboardHeight: number
  /** Bottom safe-area inset. */
  insetBottom: number
  /** Full screen height. */
  screenHeight: number
  /** Current scroll offset (contentOffset.y). */
  scrollOffset: number
  /** Total scrollable content height. */
  contentHeight: number
}

export function computeKeyboardScrollTarget(p: KeyboardScrollInput): number {
  // Visible area above the keyboard, inside the scroll view's bounds.
  const scrollViewBottom = p.scrollViewScreenY + p.scrollViewScreenHeight
  const visibleBottom = p.screenHeight - p.keyboardHeight - p.insetBottom
  const effectiveBottom = Math.min(scrollViewBottom, visibleBottom)
  const visibleArea = Math.max(effectiveBottom - p.scrollViewScreenY, 1)

  // Input's screen-space Y → scroll-content Y.
  const inputContentY = p.inputScreenY - p.scrollViewScreenY + p.scrollOffset

  // Centre the input within the visible area.
  const targetY = inputContentY - (visibleArea / 2 - p.inputHeight / 2)

  const maxScroll = Math.max(0, p.contentHeight - visibleArea)
  return Math.max(0, Math.min(targetY, maxScroll))
}
